import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { ArrowLeft, ArrowRight, Bell, CalendarDays, FileLock2, HeartHandshake, History, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/site/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, PageShell } from "@/components/ui/shields-layout";
import { getManagedHomeOverview, setActiveHome, updateHomeDetails } from "@/lib/homes.functions";

export const Route = createFileRoute("/_authenticated/home/$homeId")({
  beforeLoad: () => { throw redirect({ to: "/guides" }); },
  head: () => ({ meta: [{ title: "Manage home — Shields" }, { name: "robots", content: "noindex" }] }),
  component: ManageHomePage,
});

function ManageHomePage() {
  const { homeId } = Route.useParams();
  const client = useQueryClient();
  const [editing, setEditing] = useState(false);
  const overview = useQuery({
    queryKey: ["managed-home-overview", homeId],
    queryFn: () => getManagedHomeOverview({ data: { homeId } }),
    retry: false,
  });
  const save = useMutation({
    mutationFn: (details: { label: string; addressLine: string; postcode: string }) =>
      updateHomeDetails({ data: { homeId, ...details } }),
    onSuccess: async (result) => {
      if (!result.ok) { toast.error(result.error); return; }
      await Promise.all([
        client.invalidateQueries({ queryKey: ["managed-home-overview", homeId] }),
        client.invalidateQueries({ queryKey: ["managed-homes"] }),
        client.invalidateQueries({ queryKey: ["homes"] }),
        client.invalidateQueries({ queryKey: ["feed-centre"] }),
      ]);
      setEditing(false);
      toast.success("Home details saved");
    },
    onError: () => toast.error("Could not save your home. Please try again."),
  });
  const activate = useMutation({
    mutationFn: () => setActiveHome({ data: { homeId } }),
    onSuccess: async (result) => {
      if (!result.ok) { toast.error(result.error); return; }
      await Promise.all([
        client.invalidateQueries({ queryKey: ["homes"] }),
        client.invalidateQueries({ queryKey: ["feed-centre"] }),
        client.invalidateQueries({ queryKey: ["feed"] }),
      ]);
      toast.success("Active home updated");
    },
    onError: () => toast.error("Could not switch your active home."),
  });

  const result = overview.data;
  const ready = result?.ok ? result : null;
  const home = ready?.home;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    save.mutate({
      label: String(form.get("label") ?? ""),
      addressLine: String(form.get("addressLine") ?? ""),
      postcode: String(form.get("postcode") ?? ""),
    });
  }

  const destinations = [
    { to: "/home/health", title: "Home Health", icon: ShieldCheck, detail: "See recorded work and maintenance dates" },
    { to: "/home/maintenance", title: "Reminders", icon: Bell, detail: "Manage maintenance reminders" },
    { to: "/home/vault", title: "Documents", icon: FileLock2, detail: "Keep certificates, warranties and manuals" },
    { to: "/home/history", title: "Service history", icon: History, detail: "View work completed through Shields" },
    { to: "/home/calendar", title: "Calendar", icon: CalendarDays, detail: "See scheduled work and reminders" },
    { to: "/home/partners", title: "Previous Partners", icon: HeartHandshake, detail: "Find professionals you have used" },
    { to: "/home/household", title: "Household", icon: Users, detail: "Manage access to your home" },
  ] as const;

  return (
    <div className="min-h-screen bg-background pb-24 text-foreground">
      <AppHeader workspace="customer" />
      <PageShell width="narrow">
        <Link to="/home" className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-primary">
          <ArrowLeft className="h-4 w-4" aria-hidden /> All homes
        </Link>
        {overview.isPending ? (
          <p role="status" className="mt-5 text-sm text-muted-foreground">Loading your home…</p>
        ) : overview.isError || !ready || !home ? (
          <section className="card-surface mt-5 p-6">
            <h1 className="text-xl font-bold">Home unavailable</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {result && !result.ok ? result.error : "We couldn’t load this home. It may have been removed or you may not have access."}
            </p>
            <Button className="mt-4" onClick={() => overview.refetch()}>Try again</Button>
          </section>
        ) : (
          <>
            <PageHeader eyebrow="Your home, handled" title={home.label} description={[home.addressLine, home.townCity, home.postcode].filter(Boolean).join(", ")} />
            <section className="card-surface mt-5 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold">Property details</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Your exact address stays private until an authorised booking stage.</p>
                </div>
                {ready.access === "owner" && !editing ? <Button variant="outline" onClick={() => setEditing(true)}>Edit home</Button> : null}
              </div>
              {editing && ready.access === "owner" ? (
                <form onSubmit={submit} className="mt-5 grid gap-4" key={home.id}>
                  <div><Label htmlFor="home-label">Home name</Label><Input id="home-label" name="label" defaultValue={home.label} required maxLength={40} className="mt-1 min-h-11" /></div>
                  <div><Label htmlFor="home-address">Address line</Label><Input id="home-address" name="addressLine" defaultValue={home.addressLine ?? ""} maxLength={120} className="mt-1 min-h-11" /></div>
                  <div><Label htmlFor="home-postcode">Postcode</Label><Input id="home-postcode" name="postcode" defaultValue={home.postcode} required maxLength={10} className="mt-1 min-h-11" /></div>
                  <div className="flex gap-2"><Button type="submit" disabled={save.isPending}>{save.isPending ? "Saving…" : "Save changes"}</Button><Button type="button" variant="outline" onClick={() => setEditing(false)}>Cancel</Button></div>
                </form>
              ) : (
                <div className="mt-4 text-sm">
                  <p>{[home.addressLine, home.townCity].filter(Boolean).join(", ") || "Address line not added"}</p>
                  <p className="mt-1 font-semibold">{home.postcode}</p>
                  {ready.access === "owner" ? <Button variant="outline" className="mt-4" disabled={activate.isPending} onClick={() => activate.mutate()}>Use for bookings and nearby activity</Button> : <p className="mt-3 text-muted-foreground">Shared with you by this home's owner.</p>}
                </div>
              )}
            </section>

            <section className="mt-6 grid gap-3 sm:grid-cols-2" aria-label="Manage this home">
              {destinations.filter(({ to }) => ready.access === "owner" || (to !== "/home/maintenance" && to !== "/home/household")).map(({ to, icon: Icon, title, detail }) => (
                <Link key={to} to={to} search={{ homeId }} className="card-surface flex min-h-24 items-center gap-3 p-4 transition hover:border-primary/50 focus-visible:outline-2 focus-visible:outline-primary">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary"><Icon className="h-5 w-5" aria-hidden /></span>
                  <span className="min-w-0 flex-1"><strong className="block">{title}</strong><small className="mt-1 block text-sm text-muted-foreground">{detail}</small></span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                </Link>
              ))}
            </section>
            <section className="card-surface mt-6 p-5">
              <h2 className="font-bold">This home's activity</h2>
              <p className="mt-2 text-sm text-muted-foreground">{ready.upcoming.length} active job{ready.upcoming.length === 1 ? "" : "s"} · {ready.completed.length} completed · {ready.reminders.length} active reminder{ready.reminders.length === 1 ? "" : "s"}</p>
              {ready.access === "owner" ? <Link to="/book" search={{ homeId }} className="mt-4 inline-flex min-h-11 items-center rounded-xl bg-primary px-4 text-sm font-bold text-primary-foreground">Book a service for this home</Link> : null}
            </section>
          </>
        )}
      </PageShell>
    </div>
  );
}
